import { LitElement, css, html, nothing } from "lit";
import { fa, onceParentAvailable } from "../../utils/widgets.js";
import { faSvg, select, titles, panel } from "../styles.js";
import { GPS_VALUES } from "../../utils/utils.js";
import { faEarthEurope, faInfoCircle, faPalette, faXmark } from "@fortawesome/free-solid-svg-icons";
import { MAP_THEMES_STYLES } from "../../utils/map.js";
import { classMap } from "lit/directives/class-map.js";
import { DISABLED_LEVEL, initIndoorEqualOnMap } from "../../utils/indoor.js";


/**
 * Map Layers menu allows user to select background and map theme.
 * @class Panoramax.components.menus.MapLayers
 * @element pnx-map-layers-menu
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-map-layers-menu _parent=${viewer} />
 * ```
 */
export default class MapLayers extends LitElement {
	/** @private */
	static styles = [ panel, faSvg, titles, select, css`
		:host {
			display: flex;
			flex-direction: column;
			align-items: end;
			gap: 5px;
			max-width: calc(100vw - 20px);
		}

		.btn-close {
			position: absolute;
			top: 8px;
			right: 5px;
			z-index: 10;
		}

		.btn-close svg {
			height: 16px;
		}

		.pnx-panel {
			position: relative;
			min-width: unset;
		}

		.map-layers {
			max-width: 100%;
			overflow-x: auto;
			padding: 10px 0;
		}
		
		.map-layers-blocks { display: flex; }

		.map-layers-block {
			border-right: 2px solid var(--grey-pale);
  			padding: 0 15px;
		}

		.map-layers-block:last-child { border-right: none; }
		.map-layers-block-bg { padding-left: 18px; }

		.map-layers-block h4 {
			margin: 0 0 10px 0;
		}

		pnx-switch {
			padding: 0 15px 0 0;
			box-sizing: border-box;
			float: right;
		}

		.legend {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-evenly;
			padding: 5px 10px;
  			gap: 10px;
			align-items: center;
		}

		.legend-entry {
			line-height: 12px;
			display: flex;
			align-items: center;
			font-size: 1.0em;
		}

		.legend-color {
			display: inline-block;
			width: 15px;
			height: 15px;
			border-radius: 3px;
			margin-right: 5px;
		}

		.style-v2 .legend-color {
			opacity: 0.8;
		}

		.legend-score {
			justify-content: center;
			gap: 0;
		}

		@media screen and (max-width: 576px) {
			:host {
				max-width: 100%;
				align-items: center;
			}
			.map-layers-block h4 {
				font-size: 1.2rem;
				margin: 0 0 15px 0;
			}
			.map-layers {
				border: none;
				width: 100%;
				padding: 0 20px;
			}
			.map-layers-blocks {
				flex-direction: column-reverse;
				gap: 20px;
				text-align: center;
			}
			.map-layers-block {
				padding: 0;
				border: none;
			}
			pnx-switch {
				float: unset;
				padding: 0;
			}
			.legend {
				border: none;
				margin: 10px 0;
				min-height: 50px;
				background: var(--grey-very-pale);
			}
			.legend-entry {
				font-size: 0.8em;
			}
		}
	` ];

	/** @private */
	static properties = {
		theme: {state: true},
	};

	/** @private */
	connectedCallback() {
		super.connectedCallback();
		onceParentAvailable(this).then(() => this._parent?.onceMapReady?.().then(() => {
			this.theme = this._parent.mapStyleComposer.panoramaxTheme;
			this._parent.mapStyleComposer.addEventListener("theme-changed", e => this.theme = e.detail.theme);
			this._parent.mapStyleComposer.addEventListener("panoramax-changed", () => setTimeout(this.requestUpdate.bind(this), 1000));
			this._parent.mapStyleComposer.addEventListener("basemap-changed", () => this.requestUpdate());
			this._parent.map.indoorEqual?.on("levelschange", () => this.requestUpdate());
			this._parent.map.indoorEqual?.on("levelchange", () => this.requestUpdate());
		}));
	}

	/** @private */
	_onIndoorSwitchChange(e) {
		if(this._parent?.map?.indoorEqual) {
			this._parent.map.indoorEqual.setLevel(e.target.checked ? "0" : DISABLED_LEVEL);
		}
		else {
			initIndoorEqualOnMap(this._parent.map, true);
		}
	}


	/** @private */
	render() {
		const isV2 = this._parent?.mapStyleComposer?._getPanoramaxStyleVersion() === 2;
		const hasIndoor = this._parent?.map?.indoorEqual || this._parent?.map?._options?.indoor?.visible === false;
		let ieq, ieqSwitch;
		if(hasIndoor) {
			ieq = this._parent.map.indoorEqual;
			ieqSwitch = html`
				<pnx-switch
					.checked=${ieq && ieq.level !== DISABLED_LEVEL}
					.disabled=${ieq && ieq.levels.length === 0}
					@change=${this._onIndoorSwitchChange.bind(this)}
					name="pnx-level-switch"
				>
					${this._parent?._t.pnx.indoor_switch}
				</pnx-switch>
			`;
		}
		const isSmall = this._parent?.isWidthSmall();
		const parentClose = () => this.parentNode.close();
		const hasGridStats = this._parent?.map?._hasGridStats?.() || false;

		return html`
			${isSmall ? nothing : html`
				<pnx-button
					class="btn-close"
					kind="inline"
					size="l"
					@click=${parentClose}
				>
					${fa(faXmark)}
				</pnx-button>
			`}

			<div class="pnx-panel map-layers">
				<div class="map-layers-blocks">
					<div class="map-layers-block">
						<h4>${fa(faPalette)} ${this._parent?._t.pnx.map_theme}</h4>
						<pnx-map-theme ._parent=${this._parent}></pnx-map-theme>
					</div>

					${hasIndoor && this._parent?.isWidthSmall() ? ieqSwitch : nothing}

					${this._parent?.mapStyleComposer?.hasManyBasemaps?.() ? html`
						<div class="map-layers-block map-layers-block-bg">
							<h4>${fa(faEarthEurope)} ${this._parent?._t.pnx.map_background}</h4>
							<pnx-map-background ._parent=${this._parent}></pnx-map-background>
						</div>
					` : nothing}
				</div>

				${hasIndoor && !this._parent?.isWidthSmall() ? ieqSwitch : nothing}
			</div>

			<div class=${classMap({
				"pnx-panel": true,
				"legend": true,
				"style-v2": isV2,
				"pnx-hidden": !hasGridStats && this.theme === MAP_THEMES_STYLES.default.id,
			})}>
				${this.theme === MAP_THEMES_STYLES.default.id && hasGridStats ? html`
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.default.color[1]}"></span>
						${this._parent?._t.pnx["map_theme_default_4"]}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.default.color[3]}"></span>
						${this._parent?._t.pnx["map_theme_default_3"]}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.default.color[5]}"></span>
						${this._parent?._t.pnx["map_theme_default_2"]}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.default.color[7]}"></span>
						${this._parent?._t.pnx["map_theme_default_1"]}
					</div>
				` : nothing}

				${this.theme === MAP_THEMES_STYLES.age.id ? html`
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.age.color[3]}"></span>
						${this._parent?._t.pnx["map_theme_age_4"]}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.age.color[5]}"></span>
						${this._parent?._t.pnx["map_theme_age_3"]}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.age.color[7]}"></span>
						${this._parent?._t.pnx["map_theme_age_2"]}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.age.color[8]}"></span>
						${this._parent?._t.pnx["map_theme_age_1"]}
					</div>
				` : nothing}

				${this.theme === MAP_THEMES_STYLES.type.id ? html`
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.type.color[3]}"></span>
						${this._parent?._t.pnx.picture_360}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.type.color[4]}"></span>
						${this._parent?._t.pnx.picture_flat}
					</div>
				` : nothing}

				${this.theme === MAP_THEMES_STYLES.score.id ? html`
					<pnx-quality-score></pnx-quality-score>
					<pnx-button
						title="${this._parent?._t.pnx.metadata_quality_help}"
						kind="superinline"
						@click=${() => this._parent?._showQualityScoreDoc()}
					>
						${fa(faInfoCircle)}
					</pnx-button>
				` : nothing}

				${this.theme === MAP_THEMES_STYLES.gps.id ? html`
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.gps.color[1]}"></span>
						${GPS_VALUES[0].label}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.gps.color[3]}"></span>
						${GPS_VALUES[1].label}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.gps.color[5]}"></span>
						${GPS_VALUES[2].label}
					</div>
					<div class="legend-entry">
						<span class="legend-color" style="background-color: ${MAP_THEMES_STYLES.gps.color[6]}"></span>
						${GPS_VALUES[3].label}
					</div>
				` : nothing}
			</div>
		`;
	}
}

customElements.define("pnx-map-layers-menu", MapLayers);
